<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import sStorageWrapper from "@/api/local";

import type { DropdownItem, TabItem } from "@/types/ui";
import type { IntegrationDriver } from "@/types/integrationInstance";
import type { TabType } from "@/types/app";

type IntegrationsFilter = {
  activeTab?: TabType
  searchText?: string
};

import { useWindowDimension } from "@/mixins/windowDimension";
import { focusInput } from "@/mixins/device";
import { getPreviousRoute } from "@/mixins/router";

import UCSearch from "@/components/ui/UCSearch.vue";
import TabMenu from "@/components/ui/TabMenu.vue";
import DropdownMenu from "@/components/ui/DropdownMenu.vue";
import IntegrationList from "@/components/integration/IntegrationList.vue";
import DockList from "@/components/dock/DockList.vue";
import AddDevice from "@/components/elements/entity/AddDevice.vue";
import AddIntegration from "@/components/integration/AddIntegration.vue";
import ImportCustomIntegration from "@/components/integration/ImportCustomIntegration.vue";
import AddDock from "@/components/dock/AddDock.vue";

const { t } = useTranslation();
const route = useRoute();
const router = useRouter();
const { isSmallScreen } = useWindowDimension();

const FILTER_STORE_KEY = 'integrations';

const storedFilters = ref<{ [key: string]: any }>({});

const searchText = ref("");
const modeAdvanced = ref(false);

const elIntegrationList = ref();
const elAddDevice = ref();
const elAddIntegration = ref();
const elImportCustomIntegration = ref();
const elAddDock = ref();

const defaultTabItem = {
  label: t('integration.integrations'),
  value: 'integration',
}

const tabItems = [
  {
    label: t('integration.integrations'),
    value: 'integration',
  },
  {
    label: t('dock.docks'),
    value: 'dock',
  },
]

const integrationAddLinks = [
  {
    icon: 'fa-light fa-puzzle',
    label: 'integration.add.title',
    value: 'add',
  },
  {
    icon: 'fa-light fa-puzzle',
    label: 'integration.install_custom.trigger',
    value: 'install',
  },
];

const activeTab = ref<TabItem>(defaultTabItem);
const elPageDevicesTools = ref();
const init = ref(true);

watch(activeTab, () => {
  if (init.value) {
    return;
  }
  if (elPageDevicesTools.value) {
    focusInput(elPageDevicesTools.value);
  }
  saveToStorage();
});

watch(searchText, () => {
  saveToStorage();
});

function goTo(item: TabItem) {
  activeTab.value = item;

  router.push({
  path: route.path,
    query: {
      ...route.query,
      category: item.value?.toLowerCase()
    }
  });
}

function goToAddIntegration(item: DropdownItem) {
  if (item.value == 'add') {
    elAddDevice.value.open();
  }

  if (item.value == 'install') {
    elImportCustomIntegration.value.open();
  }
}

function startDriverSetup(driver: IntegrationDriver) {
  elAddIntegration.value.startSetup(driver);
}

function startDriverRegister(driver: IntegrationDriver) {
  elAddIntegration.value.doStartRegisterExternal(driver);
}

function saveToStorage() {
  storedFilters.value[FILTER_STORE_KEY] = {
    activeTab: activeTab.value,
    searchText: searchText.value
  } satisfies IntegrationsFilter;

  sStorageWrapper.setValue('filters', storedFilters.value);
}

onMounted(() => {
  init.value = true;
  const currentPagePath = router.options?.history?.state?.current ? String(router.options.history.state.current) : '';
  const prevPagePath = getPreviousRoute();
  storedFilters.value = sStorageWrapper.getValue('filters') ?? {};
  let filterData = {};

  if (FILTER_STORE_KEY in storedFilters.value) {
    filterData = storedFilters.value[FILTER_STORE_KEY] satisfies IntegrationsFilter;
  }

  if (Object.keys(filterData).length > 0) {
    const storedData = filterData as IntegrationsFilter;

    goTo(storedData.activeTab || defaultTabItem);
    searchText.value = storedData.searchText || '';
  } else if (currentPagePath.includes('category=dock') || prevPagePath.includes('/dock') || prevPagePath.includes('category=dock')) {
    goTo(tabItems[1]);
  }
  init.value = false;
});

</script>
<template>
  <div class="page-devices">
    <div ref="elPageDevicesTools" class="page-devices__tools">
      <TabMenu
        @item-click="goTo"
        :list-data="tabItems"
        :active-tab="activeTab"
        :compact="true"
      />
      <div class="page-devices__tools__filter">
        <div class="page-devices__tools__filter__search">
          <UCSearch
            v-model="searchText"
            :debouncing="true"
            :small="true"
          />
        </div>
      </div>
      <div class="page-devices__tools__filter__options">
        <DropdownMenu
          v-if="activeTab.value === 'integration'"
          @item-click="goToAddIntegration"
          :list-data="integrationAddLinks"
          :on-right="true"
        >
          <template #trigger>
            <button
              class="button button--primary"
              :class="{'button--hybrid': !isSmallScreen, 'button--secondary button--icon': isSmallScreen}">
              <i class="fa-light fa-plus"></i>
              <span v-if="!isSmallScreen">{{ $t('ui.add_new') }}</span>
            </button>
          </template>
        </DropdownMenu>
        <button
          v-else-if="activeTab.value === 'dock'"
          @click="elAddDock.open()"
          class="button button--primary"
          :class="{'button--hybrid': !isSmallScreen, 'button--secondary button--icon': isSmallScreen}">
          <i class="fa-light fa-plus"></i>
          <span v-if="!isSmallScreen">{{ $t('integrations.add_new_dock') }}</span>
        </button>
      </div>
    </div>
    <div class="page-devices__body">
      <div v-if="activeTab.value === 'integration'" class="page-devices__body__inner">
        <IntegrationList
          ref="elIntegrationList"
          :search-text="searchText"
        />
      </div>
      <div v-if="activeTab.value === 'dock'" class="page-devices__body__inner">
        <DockList 
          :search-text="searchText"
        />
      </div>
    </div>
  </div>
  <AddDevice
    ref="elAddDevice"
    :only-integrations="true"
    @start-driver-setup="startDriverSetup"
    @start-driver-register="startDriverRegister"
    @change-mode-advanced="(mode: boolean) =>{modeAdvanced = mode}"
  />
  <AddIntegration
    ref="elAddIntegration"
    :mode-advanced="modeAdvanced"
  />
  <ImportCustomIntegration
    ref="elImportCustomIntegration"
    @imported="elIntegrationList.loadData()"
  />
  <AddDock
    ref="elAddDock"
  />
</template>
